import { ReactNode, useState, useEffect, useCallback } from "react";
import { useConfig } from "hooks/useConfig/useConfig";

import { CookieConsentContext } from "./CookieConsentContext";
import { ConsentPreferences, CONSENT_STORAGE_KEY, DEFAULT_PREFERENCES } from "./types";
import { updateGoogleConsentMode, loadMetaPixel } from "./consentModeUtils";

type CookieConsentProviderProps = {
  children: ReactNode;
};

const getStoredPreferences = (): ConsentPreferences | null => {
  if (typeof window === "undefined") return null;

  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as ConsentPreferences;
      return { ...parsed, necessary: true };
    }
  } catch {
    // Invalid stored data, return null
  }
  return null;
};

const savePreferences = (preferences: ConsentPreferences): void => {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // Storage not available
  }
};

const applyConsentPreferences = (preferences: ConsentPreferences, pixelId: string): void => {
  // Update Google Consent Mode
  updateGoogleConsentMode(preferences);

  // Load Meta Pixel if advertising consent is given
  if (preferences.advertising && pixelId) {
    loadMetaPixel(pixelId);
  }
};

export const CookieConsentProvider = ({ children }: CookieConsentProviderProps) => {
  const [preferences, setPreferences] = useState<ConsentPreferences | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const { META_PIXEL_TRACKING_ID } = useConfig();

  useEffect(() => {
    const stored = getStoredPreferences();
    setPreferences(stored);
    setIsHydrated(true);

    // Apply stored preferences on mount
    if (stored) {
      applyConsentPreferences(stored, META_PIXEL_TRACKING_ID);
    }
  }, []);

  const updatePreferences = useCallback((newPrefs: Partial<Omit<ConsentPreferences, "necessary">>) => {
    setPreferences((current) => {
      const updated: ConsentPreferences = {
        necessary: true,
        analytics: newPrefs.analytics ?? current?.analytics ?? false,
        advertising: newPrefs.advertising ?? current?.advertising ?? false,
      };
      savePreferences(updated);
      applyConsentPreferences(updated, META_PIXEL_TRACKING_ID);
      return updated;
    });
  }, []);

  const acceptAll = useCallback(() => {
    const allAccepted: ConsentPreferences = {
      necessary: true,
      analytics: true,
      advertising: true,
    };
    setPreferences(allAccepted);
    savePreferences(allAccepted);
    applyConsentPreferences(allAccepted, META_PIXEL_TRACKING_ID);
  }, []);

  const acceptNecessaryOnly = useCallback(() => {
    const necessaryOnly: ConsentPreferences = {
      ...DEFAULT_PREFERENCES,
    };
    setPreferences(necessaryOnly);
    savePreferences(necessaryOnly);
    applyConsentPreferences(necessaryOnly, META_PIXEL_TRACKING_ID);
  }, []);

  const isConsentGiven = preferences !== null;

  return (
    <CookieConsentContext.Provider
      value={{
        preferences,
        isConsentGiven,
        isHydrated,
        updatePreferences,
        acceptAll,
        acceptNecessaryOnly,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};
